import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  getDownloadURL,
  ref,
  Storage,
  uploadBytesResumable,
} from '@angular/fire/storage';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { AccountingService } from '../../../services/accounting.service';
import { AuthService } from '../../../services/auth.service';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';

@Component({
  selector: 'app-funding',
  standalone: true,
  imports: [
    BtnActionComponent,
    CommonModule,
    ReactiveFormsModule,
    BtnBackComponent,
    RouterLink,
    BtnActionComponent,
  ],
  templateUrl: './funding.component.html',
})
export class FundingComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private store: Store<AppState> = inject(Store<AppState>);
  private auth: AuthService = inject(AuthService);
  private accountingService: AccountingService = inject(AccountingService);
  private toastr: ToastrService = inject(ToastrService);
  private storage: Storage = inject(Storage);

  @Input() groupId: string | null = null;
  @Input() isEmbedded = false;

  userId: string | null = null;
  groupName: string = 'Group';
  group: any = null;
  fundingList: any[] = [];
  isAdmin = false;
  isTreasurer = false;

  selectedFunding: any | null = null;
  pageForm: FormGroup;
  fundingForm: FormGroup;
  isSubmitting = false; // donation
  isSubmittingFunding = false; // campaign creation
  showFundingModal = false;
  fundingFile: File | null = null;
  isUploadingFundingFile = false;
  user: any = null;

  private exit$ = new Subject<void>();

  constructor() {
    this.userId = this.auth.getUid() || null;

    this.pageForm = new FormGroup({
      amount: new FormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(100000),
      ]),
      mode: new FormControl('payment', [Validators.required]),
      currency: new FormControl('usd', [Validators.required]),
    });

    this.fundingForm = new FormGroup({
      title: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
      ]),
      description: new FormControl('', [Validators.required]),
      raiseAmount: new FormControl('', [
        Validators.required,
        Validators.min(1),
      ]),
      minContribution: new FormControl(''),
      endDate: new FormControl('', [Validators.required]),
      requesterName: new FormControl('', [Validators.required]),
      requesterContact: new FormControl('', [Validators.required]),
      isPublic: new FormControl(true, [Validators.required]),
    });

    this.store
      .select((state: any) => state.user?.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  getCurrencySymbol(): string {
    const currency = (this.group?.currency || 'usd').toLowerCase();
    switch (currency) {
      case 'eur':
        return '€';
      case 'gbp':
        return '£';
      case 'jpy':
        return '¥';
      case 'inr':
        return '₹';
      case 'usd':
      case 'cad':
      case 'aud':
      case 'nzd':
      default:
        return '$';
    }
  }

  ngOnInit(): void {
    if (!this.groupId) {
      this.groupId = this.route.snapshot.paramMap.get('id');
    }
    if (!this.groupId) {
      this.toastr.error('Group ID not found');
      return;
    }

    combineLatest([
      this.store.select(groupSelector.getGroupById(this.groupId)),
      this.accountingService.getFunding(this.groupId),
      this.store.select(groupSelector.getMyRolesInGroup(this.groupId)),
    ])
      .pipe(takeUntil(this.exit$))
      .subscribe(([group, funding, roles]) => {
        if (group) {
          this.groupName = group.name;
          this.group = group;
          this.pageForm.patchValue({ currency: group.currency || 'usd' });
        }

        if (roles) {
          this.isAdmin = roles.admin === true;
          this.isTreasurer = roles.treasurer === true;
        }

        if (funding) {
          // If the user is treasurer or admin, they should see all campaigns (active, inactive, etc.)
          // Otherwise, they only see active campaigns.
          if (this.isTreasurer || this.isAdmin) {
            this.fundingList = funding;
          } else {
            this.fundingList = funding.filter((f) => f.status === 'active');
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  selectFundingForDonation(funding: any) {
    this.selectedFunding = funding;
    this.pageForm.patchValue({ amount: '' });
  }

  cancelDonation() {
    this.selectedFunding = null;
  }

  async donate() {
    if (this.pageForm.invalid || !this.groupId || !this.selectedFunding) return;
    this.isSubmitting = true;

    try {
      const amount = this.pageForm.value.amount;
      const currency = this.pageForm.value.currency;
      const mode = this.pageForm.value.mode;

      const successUrl = `${window.location.origin}/billing/success`;
      const cancelUrl = window.location.href;

      const session =
        await this.accountingService.createDonationCheckoutSession({
          amount,
          currency,
          mode,
          groupId: this.groupId,
          fundingId: this.selectedFunding.id,
          successUrl,
          cancelUrl,
        });

      if (session?.url) {
        window.location.href = session.url;
      } else {
        this.toastr.error('Failed to initiate checkout session');
        this.isSubmitting = false;
      }
    } catch (error: any) {
      this.toastr.error(
        error.message || 'An error occurred. Please try again.',
      );
      this.isSubmitting = false;
    }
  }

  onFundingFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.toastr.error('File size exceeds 5MB limit');
      this.fundingFile = null;
      event.target.value = '';
      return;
    }

    this.fundingFile = file;
  }

  private uploadFundingFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const fileId = `${Date.now()}_${file.name}`;
      const storageRef = ref(
        this.storage,
        `groups/${this.groupId}/funding/${fileId}`,
      );
      const metadata = { contentType: file.type };
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      uploadTask.on(
        'state_changed',
        null,
        (error) => reject(error),
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then(
            (downloadURL) => resolve(downloadURL),
            (err) => reject(err),
          );
        },
      );
    });
  }

  async saveFunding() {
    if (this.fundingForm.invalid || !this.groupId) return;

    if (this.fundingList.length >= 5) {
      this.toastr.error(
        'Maximum limit of 5 funding requests reached. Please deactivate or remove an existing funding request.',
      );
      return;
    }

    this.isSubmittingFunding = true;

    try {
      let fileUrl = '';
      if (this.fundingFile) {
        this.isUploadingFundingFile = true;
        try {
          fileUrl = await this.uploadFundingFile(this.fundingFile);
        } catch (uploadErr: any) {
          this.toastr.error('Failed to upload attachment file.');
          this.isUploadingFundingFile = false;
          this.isSubmittingFunding = false;
          return;
        }
        this.isUploadingFundingFile = false;
      }

      const funding: any = {
        title: this.fundingForm.value.title,
        description: this.fundingForm.value.description,
        targetAmount: parseFloat(this.fundingForm.value.raiseAmount),
        raiseAmount: parseFloat(this.fundingForm.value.raiseAmount),
        minContribution: this.fundingForm.value.minContribution
          ? parseFloat(this.fundingForm.value.minContribution)
          : null,
        endDate: this.fundingForm.value.endDate,
        requesterName: this.fundingForm.value.requesterName,
        requesterContact: this.fundingForm.value.requesterContact,
        isPublic: this.fundingForm.value.isPublic !== false,
        createdBy: this.user?.name || 'Unknown',
        createdById: this.userId,
        requesterId: this.userId,
        status: 'active',
        raisedAmount: 0,
      };

      if (fileUrl) {
        funding.imageUrl = fileUrl;
        funding.attachmentUrl = fileUrl;
      }

      await this.accountingService.addFunding(this.groupId, funding);
      this.toastr.success('Funding request added successfully');

      this.fundingForm.reset({
        title: '',
        description: '',
        raiseAmount: '',
        minContribution: '',
        endDate: '',
        requesterName: '',
        requesterContact: '',
        isPublic: true,
      });
      this.fundingFile = null;
      this.showFundingModal = false;
      this.isSubmittingFunding = false;
    } catch (error: any) {
      this.toastr.error(error.message || 'Failed to add funding request');
      this.isSubmittingFunding = false;
    }
  }
}
